import { useState, useEffect, useCallback } from "react";
import { ICON_BASE_PATH } from "../../../../utils/config";
import { TextBox, type TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { t } from "i18next";

interface ConfirmPasswordfiledProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  validationMessage?: string;
  onValidate?: (field: string, error?: string) => void;
  triggerValidation?: boolean;
  showStarLabel?: boolean;
  disable?: boolean;
  matchValue?: string;
  minLength?: number;
}

const ConfirmPasswordfiled: React.FC<ConfirmPasswordfiledProps> = ({
  label = "",
  value,
  onChange,
  onValidate,
  triggerValidation = false,
  showStarLabel = false,
  disable = false,
  matchValue = "",
  minLength = 8,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState("");

  const validate = useCallback(
    (val: string) => {
      let errorMsg = "";
      const trimmed = val.trim();

      if (!trimmed) {
        errorMsg = `${t("please_enter_the")} ${label}`;
      } else if (trimmed.length < minLength) {
        errorMsg = `${label} ${t("must_be_at_least")} ${minLength} ${t("characters")}`;
      } else if (matchValue && val !== matchValue) {
        errorMsg = `${label} ${t("do not match")}`;
      }

      setError(errorMsg);
      onValidate?.("confirmPassword", errorMsg || undefined);
      return !errorMsg;
    },
    [label, minLength, matchValue, onValidate],
  );

  const handleChange = (e: TextBoxChangeEvent) => {
    const raw = String(e.value ?? "");
    onChange(raw);
    if (error) validate(raw);
  };

  const handleBlur = () => {
    validate(value);
  };

  const toggleVisibility = () => setIsVisible((prev) => !prev);

  useEffect(() => {
    if (triggerValidation) validate(value);
  }, [triggerValidation, value, validate]);

  useEffect(() => {
    if (value) validate(value);
  }, [matchValue]);

  return (
    <div className="gx_input_container">
      <label
        className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
      >
        {t(label)}
        {showStarLabel && (
          <span
            className={`gx_input_star ${
              value ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div
        className={`gx_input_cont ${error ? "invalid" : "valid"} flex items-center`}
      >
        <img src={`${ICON_BASE_PATH}unlock.svg`} alt="Password" />

        <TextBox
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          type={isVisible ? "text" : "password"}
          autoComplete="off"
          maxLength={32}
          disabled={disable}
          className="gx-textbox-css"
          aria-invalid={!!error}
        />

        <img
          src={`${ICON_BASE_PATH}${isVisible ? "eyeopen.svg" : "eyeclose.svg"}`}
          alt={isVisible ? "Hide" : "Show"}
          onClick={toggleVisibility}
          className="cursor-pointer invite-code-icon"
          aria-label={isVisible ? "Hide password" : "Show password"}
        />
      </div>

      {error && <div className="gx_input_validationmessage">{error}</div>}
    </div>
  );
};

export default ConfirmPasswordfiled;
